<template>
  <HpsfirProDrawer
    v-model:open="visible"
    title="NFC URL列表"
    width="80vw"
    max-width="1200px"
    content-class="store-nfc-drawer"
    body-class="store-nfc-drawer__body"
  >
    <HpsfirProTable
      v-if="visible"
      ref="tableRef"
      export-file-name="NFC URL列表"
      row-key="name"
      :columns="columns"
      :get-data="getData"
      :loading="loading"
      :paginations="{ pageSize: 10 }"
      :hide-add="true"
      :hide-delete="true"
      :hide-edit="true"
    />
  </HpsfirProDrawer>
</template>

<script lang="ts" setup>
import { h, ref } from 'vue';
import { useApi } from '@/hooks/useApi';
import { ElMessage } from 'element-plus';
import {
  useBaseAsync,
  type HpsfirProTableColumn,
  type HpsfirProTableData,
  type HpsfirProTableFilter,
  type HpsfirProTableGetDataFunction,
} from 'hpsfir-managebase-ui';
import { normalizeBizTableColumns } from '@/utils/tableColumns';


interface TableItem {
  name: string;
  url: string;
}

interface WeappItem {
  appid: string;
  name: string;
}

const visible = ref(false);
const loading = ref(false);
const tableData = ref<TableItem[]>([]);
const currentStoreId = ref<number>(0);
const tableRef = ref<any>();

const columns = ref<HpsfirProTableColumn[]>(normalizeBizTableColumns([
  {
    name: 'name',
    label: '小程序名称',
    type: 'string',
    width: 180,
  },
  {
    name: 'url',
    label: 'NFC URL',
    type: 'string',
    render: (scope: any) => h('span', {
      class: 'store-nfc-url-text',
      draggable: 'false',
      onPointerdown: (event: PointerEvent) => event.stopPropagation(),
      onPointermove: (event: PointerEvent) => event.stopPropagation(),
      onMousedown: (event: MouseEvent) => event.stopPropagation(),
      onClick: (event: MouseEvent) => event.stopPropagation(),
    }, scope?.row?.url || '-'),
  },
]));

const loadData = async() => {
  try {
    loading.value = true;
    tableData.value = [];

    // 获取小程序列表
    const weappRes = await useApi().weappApi.listWeappweapp({
      conditions: {},
    });
    const weappList = (weappRes.data.data || []) as WeappItem[];

    // 获取每个小程序的NFC URL
    for (const weapp of weappList) {
      try {
        const urlRes = await useApi().storeApi.getNFCUrlstore({
          appid: weapp.appid,
          storeId: currentStoreId.value,
        });

        tableData.value.push({
          name: weapp.name,
          url: urlRes.data.data || '-',
        });
      } catch (error) {
        console.error('获取NFC URL失败:', error);
        tableData.value.push({
          name: weapp.name,
          url: '获取失败',
        });
      }
    }
  } catch (error) {
    console.error('加载数据失败:', error);
    ElMessage.error('加载数据失败');
  } finally {
    loading.value = false;
  }
};

const getData = useBaseAsync<HpsfirProTableGetDataFunction>((filter: HpsfirProTableFilter) => {
  const page = filter.paginations.page || 1;
  const pageSize = filter.paginations.pageSize || 10;
  const start = (page - 1) * pageSize;
  const data = tableData.value.slice(start, start + pageSize);
  return {
    total: tableData.value.length,
    data,
  } as HpsfirProTableData;
});

const show = async(storeId: number) => {
  visible.value = true;
  currentStoreId.value = storeId;
  await loadData();
  tableRef.value?.refresh?.();
};

defineExpose({
  show,
});
</script>

<style lang="scss">
.store-nfc-drawer {
  width: min(1200px, 80vw) !important;

  @media (max-width: 767px) {
    width: 100vw !important;
    max-width: 100vw !important;
  }
}

.store-nfc-drawer__body {
  padding: 12px !important;

  @media (max-width: 767px) {
    padding: 8px !important;
  }
}

.store-nfc-drawer {
  .ultra-table-row,
  .ultra-table-td,
  .ultra-table-cell-content,
  .hpsfir-table-wrap td,
  .hpsfir-table-cell-content,
  .store-nfc-url-text {
    user-select: text;
    -webkit-user-select: text;
  }
}

.store-nfc-url-text {
  display: block;
  width: 100%;
  max-width: 100%;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  cursor: text;
}
</style>
